/**
 * Shared Notification Types
 * Defines notification types, channels, sub-groups, and preferences
 * used across frontend and backend.
 */

import { type GatedFeature, getAccessPattern } from './featureAccess';
import type { LeagueKey } from './league.ts';
import type { SubscriptionTier } from './subscriptionTier.ts';

// =============================================================================
// Notification Types (53 total across 9 categories)
// =============================================================================

/** Settlement — when bets resolve */
type SettlementNotificationType =
  | 'bet_won'
  | 'bet_lost'
  | 'bet_pushed'
  | 'bet_voided'
  | 'parlay_leg_hit'
  | 'parlay_leg_lost';

/** Live Game — during active games */
type LiveGameNotificationType =
  | 'game_starting'
  | 'progress_milestone'
  | 'line_crossed'
  | 'leg_busted'
  | 'halftime_summary';

/** Social — followed users + sharing + friends */
type SocialNotificationType =
  | 'followed_user_bet'
  | 'followed_user_settled'
  | 'parlay_shared_with_you'
  | 'parlay_shared_by_you'
  | 'friend_request_received'
  | 'friend_request_accepted'
  | 'new_follower';

/** Account — subscription lifecycle + creator alerts + security + disputes */
type AccountNotificationType =
  | 'subscription_upgraded'
  | 'subscription_downgraded'
  | 'subscription_cancelled'
  | 'subscription_payment_failed'
  | 'subscription_expiring'
  | 'subscription_renewed'
  | 'subscription_dispute'
  | 'subscription_dispute_won'
  | 'subscription_dispute_lost'
  | 'subscription_dispute_banned'
  | 'welcome'
  | 'creator_connect_reminder'
  | 'creator_eligible'
  | 'creator_payout_sent'
  | 'creator_payout_failed'
  | 'account_linked'
  | 'account_unlinked'
  | 'email_changed'
  | 'password_changed';

/** System — engagement & digests + admin ops */
type SystemNotificationType =
  | 'daily_summary'
  | 'weekly_recap'
  | 'streak_alert'
  | 'quota_warning'
  | 'calibration_needed'
  | 'service_health_alert'
  | 'daily_gen_truncated';

/** Market Monitoring — line movement & sharp signals on tracked legs */
type MarketMonitoringNotificationType = 'line_movement_alert' | 'sharp_signal_alert';

/** Injury — tracked player status change */
type InjuryNotificationType = 'injury_alert';

/** Slate-wide — market intelligence for tonight's games (not tied to tracked legs) */
type SlateNotificationType =
  | 'slate_injury_alert'
  | 'slate_line_movement_alert'
  | 'slate_sharp_signal_alert';

/** Personal score: the user's proven edge over the AI switched (backlog 240) */
type EdgeNotificationType = 'edge_proven_above' | 'edge_proven_below' | 'edge_no_longer_proven';

/** All notification types */
export type NotificationType =
  | SettlementNotificationType
  | LiveGameNotificationType
  | SocialNotificationType
  | AccountNotificationType
  | SystemNotificationType
  | MarketMonitoringNotificationType
  | InjuryNotificationType
  | SlateNotificationType
  | EdgeNotificationType;

// =============================================================================
// Channels
// =============================================================================

export type NotificationChannel = 'in_app' | 'email' | 'web_push';

// =============================================================================
// Sub-Groups (19 groups for preference control)
// =============================================================================

export type NotificationSubGroup =
  | 'personalScore'
  | 'wins'
  | 'losses'
  | 'voidsAndPushes'
  | 'keyMoments'
  | 'progressTracking'
  | 'followedNewBets'
  | 'followedResults'
  | 'friendRequests'
  | 'subscription'
  | 'accountAlerts'
  | 'summaries'
  | 'alerts'
  | 'lineMovementAlerts'
  | 'sharpSignalAlerts'
  | 'injuryAlerts'
  | 'slateInjuryAlerts'
  | 'slateLineMovementAlerts'
  | 'slateSharpSignalAlerts';

/** Maps each notification type to its sub-group */
export const NOTIFICATION_SUB_GROUPS: Record<NotificationType, NotificationSubGroup> = {
  // Settlement
  bet_won: 'wins',
  parlay_leg_hit: 'wins',
  bet_lost: 'losses',
  parlay_leg_lost: 'losses',
  bet_pushed: 'voidsAndPushes',
  bet_voided: 'voidsAndPushes',
  // Live Game
  line_crossed: 'keyMoments',
  leg_busted: 'keyMoments',
  progress_milestone: 'progressTracking',
  halftime_summary: 'progressTracking',
  game_starting: 'progressTracking',
  // Social
  followed_user_bet: 'followedNewBets',
  followed_user_settled: 'followedResults',
  parlay_shared_with_you: 'followedResults',
  parlay_shared_by_you: 'followedResults',
  friend_request_received: 'friendRequests',
  friend_request_accepted: 'friendRequests',
  new_follower: 'friendRequests',
  // Account
  subscription_upgraded: 'subscription',
  subscription_downgraded: 'subscription',
  subscription_cancelled: 'subscription',
  subscription_payment_failed: 'subscription',
  subscription_expiring: 'subscription',
  subscription_renewed: 'subscription',
  subscription_dispute: 'subscription',
  subscription_dispute_won: 'subscription',
  subscription_dispute_lost: 'subscription',
  subscription_dispute_banned: 'subscription',
  welcome: 'subscription',
  creator_connect_reminder: 'accountAlerts',
  creator_eligible: 'accountAlerts',
  creator_payout_sent: 'accountAlerts',
  creator_payout_failed: 'accountAlerts',
  account_linked: 'accountAlerts',
  account_unlinked: 'accountAlerts',
  email_changed: 'accountAlerts',
  password_changed: 'accountAlerts',
  // System
  daily_summary: 'summaries',
  weekly_recap: 'summaries',
  streak_alert: 'alerts',
  quota_warning: 'alerts',
  calibration_needed: 'alerts',
  service_health_alert: 'alerts',
  daily_gen_truncated: 'alerts',
  // Market Monitoring
  line_movement_alert: 'lineMovementAlerts',
  sharp_signal_alert: 'sharpSignalAlerts',
  // Injury
  injury_alert: 'injuryAlerts',
  // Slate-wide (market intelligence)
  slate_injury_alert: 'slateInjuryAlerts',
  slate_line_movement_alert: 'slateLineMovementAlerts',
  slate_sharp_signal_alert: 'slateSharpSignalAlerts',
  // Personal score
  edge_proven_above: 'personalScore',
  edge_proven_below: 'personalScore',
  edge_no_longer_proven: 'personalScore',
};

// =============================================================================
// Locked Notification Types (always delivered via in_app + email, ignores prefs)
// =============================================================================

/** Security-critical notification types that cannot be disabled by user preferences */
export const LOCKED_NOTIFICATION_TYPES: Set<NotificationType> = new Set([
  'email_changed',
  'password_changed',
  'subscription_payment_failed',
  'subscription_dispute',
  'subscription_dispute_won',
  'subscription_dispute_lost',
  'subscription_dispute_banned',
]);

// =============================================================================
// Preferences
// =============================================================================

export interface ChannelToggles {
  inApp: boolean;
  email: boolean;
  webPush: boolean;
}

export type NotificationPreferences = Record<NotificationSubGroup, ChannelToggles>;

/** Default preferences — all in-app on, email/push off except subscription (email on) */
export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  friendRequests: { inApp: true, email: false, webPush: false },
  wins: { inApp: true, email: false, webPush: false },
  losses: { inApp: true, email: false, webPush: false },
  voidsAndPushes: { inApp: true, email: false, webPush: false },
  keyMoments: { inApp: true, email: false, webPush: false },
  progressTracking: { inApp: true, email: false, webPush: false },
  followedNewBets: { inApp: true, email: false, webPush: false },
  followedResults: { inApp: true, email: false, webPush: false },
  subscription: { inApp: true, email: true, webPush: false },
  accountAlerts: { inApp: true, email: true, webPush: false },
  summaries: { inApp: true, email: false, webPush: false },
  alerts: { inApp: true, email: false, webPush: false },
  lineMovementAlerts: { inApp: true, email: false, webPush: true },
  sharpSignalAlerts: { inApp: true, email: false, webPush: true },
  injuryAlerts: { inApp: true, email: false, webPush: true },
  slateInjuryAlerts: { inApp: true, email: false, webPush: true },
  slateLineMovementAlerts: { inApp: true, email: false, webPush: true },
  slateSharpSignalAlerts: { inApp: true, email: false, webPush: true },
  personalScore: { inApp: true, email: false, webPush: true },
};

// =============================================================================
// Sub-Group Metadata (for settings UI)
// =============================================================================

export interface SubGroupInfo {
  key: NotificationSubGroup;
  label: string;
  description: string;
  category: 'settlement' | 'liveGame' | 'social' | 'account' | 'system' | 'monitoring';
  /** The matrix key gating this row: Hidden rows are left out, Locked rows show locked. None: everyone. */
  feature?: GatedFeature;
}

export const SUB_GROUP_INFO: SubGroupInfo[] = [
  // Settlement
  {
    key: 'wins',
    label: 'Wins',
    description: 'Parlay wins and individual leg hits',
    category: 'settlement',
  },
  {
    key: 'losses',
    label: 'Losses',
    description: 'Parlay losses and individual leg misses',
    category: 'settlement',
  },
  {
    key: 'voidsAndPushes',
    label: 'Voids & Pushes',
    description: 'Voided legs and pushed parlays',
    category: 'settlement',
  },
  // Live Game
  {
    key: 'keyMoments',
    label: 'Key Moments',
    description: 'Line crossed and leg busted alerts',
    category: 'liveGame',
  },
  {
    key: 'progressTracking',
    label: 'Progress Tracking',
    description: 'Milestone updates, halftime summaries, game starts',
    category: 'liveGame',
  },
  // Social
  {
    key: 'friendRequests',
    label: 'Friend Requests',
    description: 'When someone sends or accepts a friend request',
    category: 'social',
  },
  {
    key: 'followedNewBets',
    label: 'New Bets (Followed)',
    description: 'When someone you follow tracks a parlay',
    category: 'social',
  },
  {
    key: 'followedResults',
    label: 'Results (Followed)',
    description: 'When someone you follow settles a parlay',
    category: 'social',
  },
  // Account
  {
    key: 'subscription',
    label: 'Subscription',
    description: 'Upgrades, downgrades, renewals, and billing',
    category: 'account',
  },
  {
    key: 'accountAlerts',
    label: 'Account Alerts',
    description: 'Creator onboarding reminders and account actions',
    category: 'account',
  },
  // System
  {
    key: 'summaries',
    label: 'Summaries',
    description: 'Daily summaries and weekly recaps',
    category: 'system',
  },
  {
    key: 'alerts',
    label: 'Alerts',
    description: 'Win/loss streaks and quota warnings',
    category: 'system',
  },
  {
    key: 'personalScore',
    label: 'Personal Score',
    description: 'When your edge over the AI is proven, or stops being',
    category: 'system',
    feature: 'history_personal',
  },
  // Monitoring — Tracked
  {
    key: 'lineMovementAlerts',
    label: 'Line Movement',
    description: 'Alerts when lines move on your tracked legs',
    category: 'monitoring',
    feature: 'tracked_line_alerts',
  },
  {
    key: 'sharpSignalAlerts',
    label: 'Sharp Signals',
    description: 'Alerts when RLM, +EV, or sharp consensus appear on your tracked legs',
    category: 'monitoring',
    feature: 'tracked_sharp_signal_alerts',
  },
  {
    key: 'injuryAlerts',
    label: 'Injury Alerts',
    description: 'Alerts when tracked players are listed as Out or Questionable',
    category: 'monitoring',
    feature: 'tracked_injury_alerts',
  },
  // Monitoring — Slate-wide (market intelligence)
  {
    key: 'slateInjuryAlerts',
    label: 'Slate Injuries',
    description: "Any starter Out/Doubtful on tonight's games",
    category: 'monitoring',
    feature: 'slate_injury_alerts',
  },
  {
    key: 'slateLineMovementAlerts',
    label: 'Slate Line Moves',
    description: "Big line moves (2+ pts) at your books on tonight's slate",
    category: 'monitoring',
    feature: 'slate_line_movement_alerts',
  },
  {
    key: 'slateSharpSignalAlerts',
    label: 'Slate Sharp Signals',
    description: "Sharp reads (reverse line movement, consensus) on tonight's big moves",
    category: 'monitoring',
    feature: 'slate_sharp_signal_alerts',
  },
];

export interface NotificationSubGroupRow {
  info: SubGroupInfo;
  locked: boolean;
}

/** The settings rows a tier sees, by category, gated by each row's matrix key. */
export function groupNotificationSubGroups(
  tier: SubscriptionTier,
): Map<SubGroupInfo['category'], NotificationSubGroupRow[]> {
  const groups = new Map<SubGroupInfo['category'], NotificationSubGroupRow[]>();
  for (const info of SUB_GROUP_INFO) {
    const pattern = info.feature ? getAccessPattern(tier, info.feature) : 'allowed';
    if (pattern === 'hidden') continue;
    const rows = groups.get(info.category) ?? [];
    rows.push({ info, locked: pattern === 'locked' });
    groups.set(info.category, rows);
  }
  return groups;
}

/** Whether a tier can turn on slate move alerts, and so choose which books send them. */
export function canChooseSlateAlertBooks(tier: SubscriptionTier): boolean {
  return SUB_GROUP_INFO.some(
    (info) =>
      (info.key === 'slateLineMovementAlerts' || info.key === 'slateSharpSignalAlerts') &&
      info.feature !== undefined &&
      getAccessPattern(tier, info.feature) === 'allowed',
  );
}

/** The matrix key behind a sub-group, for the upgrade prompt on a Locked row. */
export function subGroupFeature(key: NotificationSubGroup): GatedFeature | undefined {
  return SUB_GROUP_INFO.find((info) => info.key === key)?.feature;
}

// =============================================================================
// Settlement Leg Summary (for enriched settlement notifications)
// =============================================================================

export interface SettlementLegSummary {
  player: string;
  team?: string;
  statType: string;
  line: number | null;
  overUnder: string | null;
  result: 'hit' | 'miss' | 'void' | 'push';
  actualValue: number | null;
}

// =============================================================================
// Notification Data — discriminated union by NotificationType
// =============================================================================
//
// Each notification type's `data` shape carries only the fields it actually
// needs. Producers must satisfy their branch (TS rejects setting fields that
// don't belong); consumers can `switch(notification.type)` and TS narrows
// `data` to exactly the branch — no `!` or `?.` for fields the branch
// guarantees.
//
// All branches share `summary: string`. Branches that pertain to a specific
// game or leg carry `leagueKey: LeagueKey` as a required field; account /
// social / system payloads have no league field at all.

/** Common to every branch — every notification has a human-readable summary. */
interface NotificationDataBase {
  summary: string;
  /** Human-readable detail text (optional secondary info shown below summary). */
  details?: string;
  /**
   * Internal dedup token set by alert producers (monitor services, the service
   * health monitor). Producers check for an existing row with the same
   * (userId, dedupKey) inside their window before sending, so a restart cannot
   * re-send. Not user-facing.
   */
  dedupKey?: string;
}

/**
 * Leg-level settlement: a single leg of a parlay resolved.
 * Variants: `parlay_leg_hit`, `parlay_leg_lost`.
 * Rendered by `LegBody.tsx` / mobile equivalent.
 */
export interface LegNotificationData extends NotificationDataBase {
  parlayId: number;
  legId: number;
  leagueKey: LeagueKey;
  player?: string;
  team?: string;
  opponentTeam?: string;
  statType?: string;
  /** Full prop display string (e.g. "San Antonio Spurs -7.5", "Under 224.5"). */
  prop?: string;
  odds?: string;
}

/**
 * Parlay-level settlement: the whole parlay resolved.
 * Variants: `bet_won`, `bet_lost`, `bet_pushed`, `bet_voided`.
 * Rendered by `SettlementBody.tsx` / mobile equivalent.
 *
 * Note: settlement notifications don't carry `leagueKey` at the top level —
 * a parlay can mix leagues, and the per-leg `legs[]` array carries the league
 * context each leg needs.
 */
export interface SettlementNotificationData extends NotificationDataBase {
  parlayId: number;
  odds?: string;
  legs: SettlementLegSummary[];
}

/**
 * Live-game updates while a tracked parlay's games are in progress.
 * Variants: `game_starting`, `progress_milestone`, `line_crossed`,
 * `leg_busted`, `halftime_summary`.
 */
export interface LiveGameNotificationData extends NotificationDataBase {
  parlayId?: number;
  legId?: number;
  gameId: string;
  leagueKey: LeagueKey;
  player?: string;
  team?: string;
  opponentTeam?: string;
  statType?: string;
  prop?: string;
}

/**
 * Social: another user's activity affecting this user (followed bets,
 * shares, friend requests, new followers).
 * Variants: `followed_user_bet`, `followed_user_settled`,
 * `parlay_shared_with_you`, `parlay_shared_by_you`,
 * `friend_request_received`, `friend_request_accepted`, `new_follower`.
 * No `leagueKey` — these notifications are about user actions, not specific
 * games. League info (if needed for display) is in the linked parlay.
 */
export interface SocialNotificationData extends NotificationDataBase {
  actorUserId?: string;
  actorDisplayName?: string;
  /** Linked parlay (for shared bets / followed bets); nav target on tap. */
  parlayId?: number;
  count?: number;
  odds?: string;
}

/**
 * Account + system: subscription lifecycle, creator events, security alerts,
 * digests, admin ops. No game or league context.
 * Variants: all `subscription_*`, `creator_*`, `account_*`, `welcome`,
 * `email_changed`, `password_changed`, plus all System types.
 */
export interface AccountSystemNotificationData extends NotificationDataBase {
  amount?: number;
  streakLength?: number;
  oauthProvider?: 'google' | 'apple';
}

/**
 * Market intelligence alerts on tracked legs and slate-wide market state.
 * Variants: `line_movement_alert`, `sharp_signal_alert`, `injury_alert`,
 * `slate_injury_alert`, `slate_line_movement_alert`, `slate_sharp_signal_alert`.
 * Rendered by `MonitoringBody.tsx` / mobile equivalent.
 */
export interface MonitoringNotificationData extends NotificationDataBase {
  /** Tracked-leg variants set both; slate variants set gameId only. */
  parlayId?: number;
  legId?: number;
  gameId?: string;
  leagueKey: LeagueKey;
  player?: string;
  team?: string;
  opponentTeam?: string;
  statType?: string;
  prop?: string;
  overUnder?: string;
  /** Line-movement variants. */
  lineMovement?: number;
  openingLine?: number;
  currentLine?: number;
  /** Sharp-signal variants. */
  sharpSignalType?: 'rlm' | 'positive_ev' | 'consensus' | 'sharp_line';
  /** EV percentage edge (for positive_ev sharp signals). */
  evPercent?: number;
  /** Injury variants. */
  injuryStatus?: 'Out' | 'Out For Season' | 'Questionable' | 'Doubtful';
  previousInjuryStatus?: 'Out' | 'Out For Season' | 'Questionable' | 'Doubtful';
  /** Slate move variants: the book whose line moved. */
  sportsbook?: string;
}

/**
 * Maps each NotificationType to its data branch. Producers and consumers
 * use `NotificationDataFor<T>` to get the precise data shape for a known
 * type; `NotificationDataUnion` below covers cases where the type isn't
 * known at compile time.
 */
export type NotificationDataMap = {
  // Leg
  parlay_leg_hit: LegNotificationData;
  parlay_leg_lost: LegNotificationData;
  // Settlement
  bet_won: SettlementNotificationData;
  bet_lost: SettlementNotificationData;
  bet_pushed: SettlementNotificationData;
  bet_voided: SettlementNotificationData;
  // Live game
  game_starting: LiveGameNotificationData;
  progress_milestone: LiveGameNotificationData;
  line_crossed: LiveGameNotificationData;
  leg_busted: LiveGameNotificationData;
  halftime_summary: LiveGameNotificationData;
  // Social
  followed_user_bet: SocialNotificationData;
  followed_user_settled: SocialNotificationData;
  parlay_shared_with_you: SocialNotificationData;
  parlay_shared_by_you: SocialNotificationData;
  friend_request_received: SocialNotificationData;
  friend_request_accepted: SocialNotificationData;
  new_follower: SocialNotificationData;
  // Account
  subscription_upgraded: AccountSystemNotificationData;
  subscription_downgraded: AccountSystemNotificationData;
  subscription_cancelled: AccountSystemNotificationData;
  subscription_payment_failed: AccountSystemNotificationData;
  subscription_expiring: AccountSystemNotificationData;
  subscription_renewed: AccountSystemNotificationData;
  subscription_dispute: AccountSystemNotificationData;
  subscription_dispute_won: AccountSystemNotificationData;
  subscription_dispute_lost: AccountSystemNotificationData;
  subscription_dispute_banned: AccountSystemNotificationData;
  welcome: AccountSystemNotificationData;
  creator_connect_reminder: AccountSystemNotificationData;
  creator_eligible: AccountSystemNotificationData;
  creator_payout_sent: AccountSystemNotificationData;
  creator_payout_failed: AccountSystemNotificationData;
  account_linked: AccountSystemNotificationData;
  account_unlinked: AccountSystemNotificationData;
  email_changed: AccountSystemNotificationData;
  password_changed: AccountSystemNotificationData;
  // System
  daily_summary: AccountSystemNotificationData;
  weekly_recap: AccountSystemNotificationData;
  streak_alert: AccountSystemNotificationData;
  quota_warning: AccountSystemNotificationData;
  calibration_needed: AccountSystemNotificationData;
  service_health_alert: AccountSystemNotificationData;
  daily_gen_truncated: AccountSystemNotificationData;
  // Market monitoring
  line_movement_alert: MonitoringNotificationData;
  sharp_signal_alert: MonitoringNotificationData;
  injury_alert: MonitoringNotificationData;
  // Slate
  slate_injury_alert: MonitoringNotificationData;
  slate_line_movement_alert: MonitoringNotificationData;
  slate_sharp_signal_alert: MonitoringNotificationData;
  // Personal score
  edge_proven_above: AccountSystemNotificationData;
  edge_proven_below: AccountSystemNotificationData;
  edge_no_longer_proven: AccountSystemNotificationData;
};

/** Get the precise data shape for a specific notification type. */
export type NotificationDataFor<T extends NotificationType> = NotificationDataMap[T];

/**
 * Discriminated union of all possible data payloads. The final shape
 * `Notification.data` will narrow to once the migration completes. Use this
 * via `switch (notification.type)` to get the precise branch in consumers
 * that have opted in to the new types.
 */
export type NotificationDataUnion =
  | LegNotificationData
  | SettlementNotificationData
  | LiveGameNotificationData
  | SocialNotificationData
  | AccountSystemNotificationData
  | MonitoringNotificationData;

/**
 * Discriminated tuple {type, data} for sites that need to keep them linked.
 * Producers building notification literals get full type checking — TS
 * rejects payloads that don't match the type's branch.
 */
export type TypedNotificationPayload = {
  [T in NotificationType]: { type: T; data: NotificationDataFor<T> };
}[NotificationType];

// =============================================================================
// Notification record
// =============================================================================

/** Envelope fields common to every Notification regardless of type. */
interface NotificationEnvelope {
  id: string;
  userId: string;
  readAt: string | null;
  channels: NotificationChannel[];
  createdAt: string;
}

/**
 * Full notification record as returned to the frontend. Discriminated
 * by `type` — `switch (notification.type)` narrows `data` to the matching
 * branch with no casts needed.
 */
export type Notification = {
  [T in NotificationType]: NotificationEnvelope & {
    type: T;
    data: NotificationDataFor<T>;
  };
}[NotificationType];

/**
 * Notification enriched with actor profile data (for activity feed).
 * Distributing the intersection over the union preserves the discriminated
 * structure — `switch (feedNotification.type)` still narrows `data`.
 */
export type FeedNotification = Notification & {
  user?: {
    id: string;
    displayName: string | null;
    isVerified: boolean;
    followerCount: number;
    currentRoi: number;
    currentWinRate: number;
    currentStreak: number;
  } | null;
};
